import Agent, {
  type AgentSuggestions,
  type AgentParams,
  type AgentInstances,
} from './Agent';
import type AssistantToolCall from '../../framework/models/AssistantToolCall';
import React from 'react';
import type AssistantViewController from '../components/AssistantView/AssistantViewController';
import type AssistedEditor from './WritingAgent/AssistedEditor';

interface WritingAgentParams {
  projectId: RecordId | undefined;
}

interface WritingAgentInstances {
  assistedEditor: AssistedEditor;
}

class WritingAgent extends Agent {
  static name = 'WritingAgent';

  static publicName = 'Writing';

  static available(
    _agentParams: AgentParams,
    agentInstances: AgentInstances
  ): boolean {
    return Boolean(
      window.currentAccount?.hasGeneralAiAssistant &&
        agentInstances.assistedEditor
    );
  }

  assistedEditor: AssistedEditor;

  constructor(
    controller: AssistantViewController,
    agentParams: WritingAgentParams,
    agentInstances: WritingAgentInstances
  ) {
    super(controller, agentParams, agentInstances);

    this.assistedEditor = agentInstances.assistedEditor;
    this.assistedEditor.attachToAssistantViewController(controller);
  }

  beforeApproveToolCall(
    toolCall: AssistantToolCall,
    data?: Record<string, unknown>
  ): Record<string, unknown> | undefined {
    if (toolCall.tool === 'WritingAgent::DocumentEditTool') {
      return this.applyDocumentEditToolCall(toolCall);
    }

    return super.beforeApproveToolCall(toolCall, data);
  }

  applyDocumentEditToolCall(toolCall: AssistantToolCall): {
    replaced: boolean;
    currentContent?: string;
  } {
    let operationType: any = 'replace_text';
    let fromText = '';
    let toText = '';

    if (toolCall.content?.data) {
      const data = JSON.parse(toolCall.content?.data) as any;
      // TODO: remove data.where once we have migrated all tool calls
      if (data && data.where) {
        operationType = data.where;
      }
      if (data && data.operation_type) {
        operationType = data.operation_type;
      }
      if (data && data.old_text) {
        fromText = data.old_text;
      }
      if (data && data.new_text) {
        toText = data.new_text;
      }
    }

    if (!toText && !fromText) {
      console.warn(
        'WritingAgent::applyDocumentEditToolCall - No text to replace, ignoring tool call'
      );
      return { replaced: false };
    }

    // Route to appropriate method based on content format
    const editorHtml = this.assistedEditor.getEditorHtml();

    if (
      this.assistedEditor.updateEditorHtml(
        editorHtml,
        toText,
        fromText,
        operationType
      )
    ) {
      return { replaced: true };
    } else {
      console.warn(
        'WritingAgent::applyDocumentEditToolCall - Failed to update editor with HTML content'
      );
      return { replaced: false, currentContent: editorHtml };
    }
  }

  getSuggestions(): AgentSuggestions {
    return [
      {
        title: 'Outline',
        prompt:
          'Tell me what you are thinking through and the level of detail that you want in your outline.',
        prefix: `Here is what I want the AI assistant to do.\n\n-> I am going to ask you to create an outline of a topic to insert at the end of the document.\n\nUse bullet points for the outline.\n\nLet's start...  \n\nThe topic is: `,
        icon: 'list',
      },
      {
        title: 'Draft',
        prompt:
          'Tell me what you are writing and share some details about what to include and the tone. You can add example text and even refer to existing Aha! records by their ID.',
        prefix: `Here is what I want the AI assistant to do.\n\n-> I am going to ask you to draft a document.\n\nBefore starting though determine if I have told you how long I want the document. If not, ask me.
Let's start...\n\nThe document is: `,
        icon: 'draft',
      },
      {
        title: 'Refine',
        prompt:
          'Tell me what text you want to refine by selecting it or pasting it and tell me how you want it improved.',
        prefix: `Here is what I want the AI assistant to do.\n\n-> I am going to ask you to refine text.\n\nBefore starting though determine if I have told you how I would like you to improve the text. If not, ask.\nLet's start...\n\nThe text is: `,
        icon: 'refine',
      },
      {
        title: 'Summarize',
        prompt: 'Tell me what you are distilling.',
        prefix: `Here is what I want the AI assistant to do.\n\n-> I am going to ask you to summarize text.\n\nBefore starting though determine if I have told you how I would like you to summarize the text. If not, ask.
Let's start...\n\nThe text is: `,
        icon: 'summarize',
      },
      {
        title: 'Expand',
        prompt:
          'Tell me what additional details you would like to add and how long it should be.',
        prefix: `Here is what I want the AI assistant to do.\n\n-> I am going to ask you to expand text.\n\nBefore starting though determine if I have told you how I would like you to expand the text. If not, ask.
Let's start...\n\nThe text is: `,
        icon: 'whiteboard',
      },
    ];
  }

  getToolActions(toolCall: AssistantToolCall): React.ReactNode {
    // The server-side buttons seem to be working well enough that we don't need this.
    if (!toolCall.content?.panel?.body) {
      return null;
    }

    const contentType = toolCall.content.panel.contentType;

    switch (contentType) {
      case 'application/vnd.aha.whiteboard':
        return this.getActionButton('Insert', () => {
          this.assistedEditor.insertWhiteboard(toolCall.id);
        });
      default:
        return null;
    }
  }

  getActionButton(content, onClick): React.ReactNode {
    return (
      <aha-button
        kind='primary'
        size='mini'
        onClick={onClick}
        key={`btn-${content}`}
      >
        {content}
      </aha-button>
    );
  }
}

Agent.register(WritingAgent);
